// BookingModal.jsx — Dynamic fields from bookingform context + saves to /api/enquiry
import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import { MyContext } from "../../../../../Context/MyContext";
import API from "../../../../../Api/api";
import { trackActivity, propertyActivityLabel } from "../../../../../utils/userActivity";
import "./BookingModal.css";

const BookingModal = ({ open, onClose, property }) => {
  const { bookingform, user } = useContext(MyContext);

const [isMobile, setIsMobile] = useState(
  window.matchMedia("(max-width: 768px)").matches
);

useEffect(() => {
  const media = window.matchMedia("(max-width: 768px)");

  const listener = (e) => setIsMobile(e.matches);

  media.addEventListener("change", listener);

  return () => media.removeEventListener("change", listener);
}, []);


  // Init form with user data pre-filled
  const [formData, setFormData] = useState({});
  const [loading,  setLoading]  = useState(false);
  const [sent,     setSent]     = useState(false);

  // Pre-fill user data when modal opens
  useEffect(() => {
    if (open && user) {
      setFormData(prev => ({
        ...prev,
        name:  prev.name  || user.name  || user.username || '',
        phone: prev.phone || user.phone || '',
        email: prev.email || user.email || '',
      }));
    }
  }, [open, user]);

  if (!open) return null;

  const fields = Array.isArray(bookingform) ? bookingform : [];

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    const missing = fields.filter(f => f.required && !formData[f.field]?.toString().trim());
    if (missing.length) {
      toast.error(`Please fill: ${missing.map(f => f.label).join(', ')}`);
      return;
    }

    setLoading(true);
    try {
      // Build message from all form fields
      const msgLines = fields
        .filter(f => formData[f.field])
        .map(f => `${f.label}: ${formData[f.field]}`)
        .join('\n');

      await API.post('/booking', {
        property_id: property?.id   || null,
        user_id:     user?.id       || null,
        name:        formData.name  || formData[fields[0]?.field] || 'Guest',
        phone:       formData.phone || formData.mobile || '',
        email:       formData.email || '',
        message:     `BOOKING REQUEST\n\n${msgLines}`,
        form_data:   formData,
        status:      'pending',
      });

      if (property?.id) {
        await trackActivity({
          type: 'booking',
          label: propertyActivityLabel(property, 'Property booked'),
          url: `/property/${property.id}`,
          property_id: property.id,
        }, user);
      }

      setSent(true);
      toast.success('Booking request sent! We\'ll confirm shortly.');
      setTimeout(() => { setSent(false); onClose(); setFormData({}); }, 2500);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Booking failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderField = (item) => {
    const common = {
      name:     item.field,
      id:       item.field,
      value:    formData[item.field] || '',
      onChange: handleChange,
      placeholder: item.placeholder || '',
      required: item.required || false,
      className: 'bm-input',
    };

    if (item.type === 'textarea') {
      return <textarea {...common} rows={3}/>;
    }
    return <input {...common} type={item.type || 'text'}/>;
  };

  return (
    <div
  className={!isMobile ? "bm-overlay bm-center" : "bm-overlay"}
  onClick={(e) => {
    if (!isMobile && e.target.classList.contains("bm-overlay")) {
      onClose();
    }
  }}
>
  <div
    className={!isMobile ? "bm-modal" : "bm-sheet"}
    onClick={e => e.stopPropagation()}
  >
        {/* Drag handle — mobile */}
        <div className="bm-drag"/>

        <div className="bm-header">
          <div className="bm-header-left">
            <div className="bm-header-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                <rect x="3" y="4" width="18" height="18" rx="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
            </div>
            <div>
              <h2 className="bm-title">Book a Site Visit</h2>
              {property && (
                <p className="bm-prop-name">
                  {property.location || property.address || `Property #${property.property_id}`}
                </p>
              )}
            </div>
          </div>
          <button className="bm-close" onClick={onClose} type="button">×</button>
        </div>

        {sent ? (
          <div className="bm-success">
            <div className="bm-success-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="52" height="52">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="9 12 11.5 14.5 15 10"/>
              </svg>
            </div>
            <h3>Booking Confirmed!</h3>
            <p>Our team will contact you shortly to schedule your visit.</p>
          </div>
        ) : (
          <form className="bm-form" onSubmit={handleSubmit} noValidate>
            <div className="bm-fields">
              {fields.length > 0 ? (
                fields.map(item => (
                  <div key={item.id || item.field} className={`bm-group${item.type === 'textarea' ? ' bm-full' : ''}`}>
                    <label className="bm-label">
                      {item.label}
                      {item.required && <span className="bm-req">*</span>}
                    </label>
                    {renderField(item)}
                  </div>
                ))
              ) : (
                // Fallback if bookingform not loaded
                <>
                  <div className="bm-group">
                    <label className="bm-label">Name <span className="bm-req">*</span></label>
                    <input className="bm-input" type="text" name="name" value={formData.name || ''} onChange={handleChange} placeholder="Your name" required/>
                  </div>
                  <div className="bm-group">
                    <label className="bm-label">Phone <span className="bm-req">*</span></label>
                    <input className="bm-input" type="tel" name="phone" value={formData.phone || ''} onChange={handleChange} placeholder="+91 00000 00000" required/>
                  </div>
                  <div className="bm-group">
                    <label className="bm-label">Preferred Date</label>
                    <input className="bm-input" type="date" name="date" value={formData.date || ''} onChange={handleChange} min={new Date().toISOString().split('T')[0]}/>
                  </div>
                  <div className="bm-group">
                    <label className="bm-label">Preferred Time</label>
                    <select className="bm-input" name="time" value={formData.time || ''} onChange={handleChange}>
                      <option value="">Any Time</option>
                      <option>9:00 AM – 11:00 AM</option>
                      <option>11:00 AM – 1:00 PM</option>
                      <option>2:00 PM – 4:00 PM</option>
                      <option>4:00 PM – 6:00 PM</option>
                    </select>
                  </div>
                </>
              )}
            </div>

            <div className="bm-footer">
              <button type="button" className="bm-cancel-btn" onClick={onClose}>Cancel</button>
              <button type="submit" className="bm-confirm-btn" disabled={loading}>
                {loading ? (
                  <><span className="bm-spinner"/> Booking…</>
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                      <rect x="3" y="4" width="18" height="18" rx="2"/>
                      <line x1="16" y1="2" x2="16" y2="6"/>
                      <line x1="8" y1="2" x2="8" y2="6"/>
                      <line x1="3" y1="10" x2="21" y2="10"/>
                    </svg>
                    Confirm Booking
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default BookingModal;